/**
 * Printable billing documents (Thai-standard A4 layout).
 *
 *  - `quotation` — ใบเสนอราคา / ใบแจ้งหนี้, issued while the invoice is open.
 *  - `receipt`   — ใบเสร็จรับเงิน, issued only after the payment is confirmed.
 *
 * The seller is NOT VAT-registered, so neither document may be labelled or
 * used as a tax invoice (ใบกำกับภาษี) and no VAT line is ever printed.
 */

export type BillingDocumentKind = 'quotation' | 'receipt';

export interface BillingSeller {
  nameTh: string;
  nameEn: string;
  address: string;
  taxId: string;
  branch: string;
  website: string;
  email?: string;
  phone?: string;
  logoUrl: string;
  bank: { name: string; accountName: string; accountNumber: string };
  lineOa: string;
}

export interface BillingCustomer {
  name: string;
  email?: string;
  phone?: string;
  taxId?: string;
  address?: string;
  contact?: string;
}

export interface BillingDocumentItem {
  name: string;
  description?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
}

export interface BillingDocumentInput {
  kind: BillingDocumentKind;
  /** Invoice reference the customer quotes when sending the slip (INV-…). */
  invoiceRef: string;
  issuedAt: string;
  dueAt?: string | null;
  paidAt?: string | null;
  customer: BillingCustomer;
  items: BillingDocumentItem[];
  total: number;
  /** Non-VAT surcharge recorded on the invoice (e.g. admin "tax" field). */
  extraCharge?: number;
  paymentMethod?: string;
  notes?: string | null;
  seller?: BillingSeller;
}

export interface RawLineItem {
  name?: string;
  quantity?: number;
  amount?: number;
  product_type?: string;
  tier?: string;
  cycle?: string;
}

export interface RawInvoiceMeta {
  product_type?: string;
  tier?: string;
  cycle?: string;
  duration_days?: number | null;
}

const origin = typeof window !== 'undefined' ? window.location.origin : '';

export const HITZ_SELLER: BillingSeller = {
  nameTh: 'บริษัท ฮิตซ์ เทค โซลูชั่นส์ จำกัด',
  nameEn: 'Hitz Tech Solutions Co., Ltd.',
  address:
    'เลขที่ 25 อาคารอัลม่า ลิงค์ ห้องเลขที่ 670 ชั้นที่ 17 ซอยชิดลม ถนนเพลินจิต แขวงลุมพินี เขตปทุมวัน กรุงเทพมหานคร 10330',
  taxId: '0105569064108',
  branch: 'สำนักงานใหญ่',
  website: 'hitzlink.com',
  email: 'support@hitzlink.com',
  logoUrl: `${origin}/invoice/company-logo.png`,
  bank: { name: 'กสิกรไทย', accountName: 'บจก. ฮิตซ์ เทค โซลูชั่นส์', accountNumber: '230-8-67655-5' },
  lineOa: '@470gpieg',
};

// ── Formatting helpers ──────────────────────────────────────────────

const THAI_DIGITS = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
const THAI_POSITIONS = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน'];

function readThaiInteger(digits: string): string {
  const trimmed = digits.replace(/^0+/, '');
  if (!trimmed) return '';
  if (trimmed.length > 6) {
    return `${readThaiInteger(trimmed.slice(0, -6))}ล้าน${readThaiInteger(trimmed.slice(-6))}`;
  }
  let out = '';
  const len = trimmed.length;
  for (let i = 0; i < len; i += 1) {
    const d = Number(trimmed[i]);
    const pos = len - i - 1;
    if (d === 0) continue;
    if (pos === 0 && d === 1 && len > 1) out += 'เอ็ด';
    else if (pos === 1 && d === 1) out += 'สิบ';
    else if (pos === 1 && d === 2) out += 'ยี่สิบ';
    else out += THAI_DIGITS[d] + THAI_POSITIONS[pos];
  }
  return out;
}

/** 269900 → "สองแสนหกหมื่นเก้าพันเก้าร้อยบาทถ้วน" */
export function bahtText(amount: number): string {
  const satangTotal = Math.round(Math.abs(Number(amount) || 0) * 100);
  const baht = Math.floor(satangTotal / 100);
  const satang = satangTotal % 100;
  if (baht === 0 && satang === 0) return 'ศูนย์บาทถ้วน';
  const bahtPart = baht > 0 ? `${readThaiInteger(String(baht))}บาท` : '';
  const satangPart = satang > 0 ? `${readThaiInteger(String(satang))}สตางค์` : 'ถ้วน';
  return bahtPart + satangPart;
}

const money = (n: number) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "14 ตุลาคม 2569" (Buddhist Era, Bangkok time). */
export function thaiDate(value?: string | Date | null): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('th-TH', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  });
}

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** INV-1a2b3c4d → RC-1a2b3c4d (same suffix keeps both documents traceable). */
export function receiptNumber(invoiceRef: string): string {
  return /^INV-/i.test(invoiceRef) ? invoiceRef.replace(/^INV-/i, 'RC-') : `RC-${invoiceRef}`;
}

/** Flattens a `billing_customers.billing_address` jsonb (or string) to one line. */
export function formatBillingAddress(address: unknown): string {
  if (!address) return '';
  if (typeof address === 'string') return address;
  if (typeof address !== 'object') return '';
  return Object.values(address as Record<string, unknown>)
    .filter((v) => typeof v === 'string' && v.trim())
    .join(' ');
}

const cycleDays = (cycle?: string, durationDays?: number | null) => {
  if (typeof durationDays === 'number' && durationDays > 0) return durationDays;
  if (cycle === 'monthly') return 30;
  if (cycle === 'yearly') return 365;
  return null;
};

/** Maps billing_invoices.line_items to printable rows (amount = unit price). */
export function itemsFromLineItems(
  lineItems: RawLineItem[] | undefined,
  meta: RawInvoiceMeta | null | undefined,
  servicePeriodStart?: string | null,
): BillingDocumentItem[] {
  const m = meta || {};
  return (lineItems || []).map((it) => {
    const productType = it.product_type || m.product_type;
    const cycle = it.cycle || m.cycle;
    const tier = it.tier || m.tier;
    const days = cycleDays(cycle, m.duration_days);

    let unit = 'รายการ';
    const parts: string[] = [];
    if (productType === 'subscription') {
      unit = 'รอบ';
      if (tier) parts.push(`แพ็กเกจ HitzLink ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`);
      if (days) parts.push(`รอบบริการ ${days} วัน`);
      if (days && servicePeriodStart) {
        const start = new Date(servicePeriodStart);
        const end = new Date(start.getTime() + days * 86400000);
        parts.push(`${thaiDate(start)} – ${thaiDate(end)}`);
      }
    } else if (productType === 'addon') {
      unit = 'ชุด';
      parts.push('บริการเสริมเพิ่มเติมจากแพ็กเกจปัจจุบัน');
    }

    return {
      name: it.name || 'ค่าบริการ',
      description: parts.join(' · ') || undefined,
      quantity: Number(it.quantity) || 1,
      unit,
      unitPrice: Number(it.amount) || 0,
    };
  });
}

// ── HTML renderer ───────────────────────────────────────────────────

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #e5e7eb; }
  body {
    font-family: 'IBM Plex Sans Thai', 'Sarabun', -apple-system, 'Segoe UI', Tahoma, sans-serif;
    color: #111827; font-size: 12.5px; line-height: 1.55;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    padding: 24px 12px;
  }
  .toolbar { max-width: 210mm; margin: 0 auto 14px; display: flex; justify-content: flex-end; gap: 8px; }
  .toolbar button {
    font: inherit; font-weight: 600; border: 0; border-radius: 8px; padding: 9px 18px; cursor: pointer;
    background: #111827; color: #fff;
  }
  .page {
    width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff;
    padding: 16mm 15mm 12mm; display: flex; flex-direction: column;
    box-shadow: 0 10px 30px rgba(0,0,0,.12); position: relative;
  }
  .head { display: flex; justify-content: space-between; gap: 18px; padding-bottom: 16px; border-bottom: 2px solid #111827; }
  .seller { display: flex; gap: 14px; }
  .seller img { width: 74px; height: 74px; object-fit: contain; flex-shrink: 0; }
  .seller-th { font-size: 17px; font-weight: 700; }
  .seller-en { font-size: 12px; font-weight: 600; color: #374151; }
  .seller-meta { font-size: 11.5px; color: #4b5563; margin-top: 4px; max-width: 360px; }
  .doc { text-align: right; min-width: 200px; }
  .doc-title { font-size: 22px; font-weight: 700; line-height: 1.2; white-space: nowrap; }
  .doc-sub { font-size: 10.5px; letter-spacing: 3px; color: #6b7280; font-weight: 600; margin-top: 2px; }
  .doc-meta { margin-top: 12px; display: grid; grid-template-columns: auto auto; gap: 2px 14px; justify-content: end; font-size: 12px; }
  .doc-meta dt { color: #6b7280; text-align: left; }
  .doc-meta dd { font-weight: 600; text-align: left; }
  .customer { margin-top: 16px; border: 1px solid #d1d5db; border-radius: 6px; padding: 12px 16px; position: relative; }
  .label { font-size: 10.5px; color: #6b7280; font-weight: 600; letter-spacing: .5px; }
  .customer-name { font-size: 15px; font-weight: 700; margin-top: 2px; }
  .customer-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 24px; margin-top: 4px; color: #374151; }
  table.items { width: 100%; border-collapse: collapse; margin-top: 16px; }
  table.items th { background: #111827; color: #fff; font-weight: 600; font-size: 11.5px; padding: 8px 10px; text-align: left; }
  table.items td { padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  table.items .c { text-align: center; }
  table.items .r { text-align: right; white-space: nowrap; }
  .item-name { font-weight: 600; }
  .item-desc { font-size: 11px; color: #6b7280; margin-top: 1px; }
  .summary { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; margin-top: 12px; }
  .words { flex: 1; background: #f3f4f6; border-radius: 4px; padding: 8px 12px; font-weight: 600; }
  .totals { width: 280px; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .totals .grand { border-top: 1px solid #111827; border-bottom: 3px double #111827; margin-top: 4px; padding: 6px 0; font-weight: 700; font-size: 14px; }
  .vat-note { margin-top: 10px; font-size: 11.5px; color: #4b5563; }
  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 16px; }
  .section-title { font-weight: 700; margin-bottom: 4px; }
  .kv { display: grid; grid-template-columns: 80px 1fr; gap: 2px 8px; margin-top: 6px; }
  .kv dt { color: #6b7280; }
  .kv dd { font-weight: 600; }
  .acct { font-size: 14px; letter-spacing: 1px; }
  ol.notes { padding-left: 18px; color: #374151; }
  ol.notes li { margin-bottom: 2px; }
  .paid-box { margin-top: 16px; border: 1px solid #86efac; background: #f0fdf4; border-radius: 6px; padding: 10px 14px; color: #14532d; }
  .stamp {
    position: absolute; top: 50%; right: 24px; transform: translateY(-50%) rotate(-10deg); background: #fff;
    border: 3px solid #15803d; color: #15803d; border-radius: 8px; padding: 4px 14px;
    font-weight: 700; font-size: 18px; letter-spacing: 2px; opacity: .55; text-align: center; line-height: 1.2;
  }
  .stamp small { display: block; font-size: 10px; letter-spacing: 1px; }
  .spacer { flex: 1; min-height: 24px; }
  .signs { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; margin-top: 24px; text-align: center; }
  .sign-line { border-top: 1px solid #9ca3af; margin: 0 20px 6px; height: 48px; }
  .sign-role { font-weight: 700; }
  .sign-meta { color: #4b5563; font-size: 11.5px; }
  .foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 10.5px; color: #6b7280; }
  @media screen and (max-width: 820px) {
    body { padding: 0; }
    .page { width: 100%; min-height: auto; padding: 18px 14px; box-shadow: none; }
    .head, .cols, .summary { flex-direction: column; display: flex; }
    .doc { text-align: left; }
    .doc-meta { justify-content: start; }
    .customer-grid { grid-template-columns: 1fr; }
    .totals { width: 100%; }
    .stamp { top: 16px; right: 16px; }
  }
  @media print {
    @page { size: A4; margin: 0; }
    html, body { background: #fff; padding: 0; }
    .toolbar { display: none; }
    .page { box-shadow: none; margin: 0; }
  }
`;

export interface RenderOptions {
  toolbar?: boolean;
  autoPrint?: boolean;
}

export function buildBillingDocumentHtml(input: BillingDocumentInput, options: RenderOptions = {}): string {
  const seller = input.seller || HITZ_SELLER;
  const isReceipt = input.kind === 'receipt';
  const docNo = isReceipt ? receiptNumber(input.invoiceRef) : input.invoiceRef;
  const docDate = isReceipt ? input.paidAt || input.issuedAt : input.issuedAt;

  const itemsSubtotal = input.items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
  let extra = Math.max(0, Number(input.extraCharge) || 0);
  const diff = itemsSubtotal + extra - input.total;
  const discount = diff > 0 ? diff : 0;
  if (diff < 0) extra += -diff;
  const afterDiscount = itemsSubtotal - discount;

  const c = input.customer;
  const customerRows = [
    c.address ? `<div style="grid-column:1/-1">ที่อยู่ ${escapeHtml(c.address)}</div>` : '',
    c.taxId ? `<div>เลขประจำตัวผู้เสียภาษี ${escapeHtml(c.taxId)}</div>` : '',
    c.contact ? `<div>ผู้ติดต่อ ${escapeHtml(c.contact)}</div>` : '',
    c.phone ? `<div>โทร ${escapeHtml(c.phone)}</div>` : '',
    c.email ? `<div>อีเมล ${escapeHtml(c.email)}</div>` : '',
  ].join('');

  const rows = input.items
    .map(
      (it, idx) => `
      <tr>
        <td class="c">${idx + 1}</td>
        <td><div class="item-name">${escapeHtml(it.name)}</div>${
          it.description ? `<div class="item-desc">${escapeHtml(it.description)}</div>` : ''
        }</td>
        <td class="c">${it.quantity}</td>
        <td class="c">${escapeHtml(it.unit)}</td>
        <td class="r">${money(it.unitPrice)}</td>
        <td class="r">${money(it.unitPrice * it.quantity)}</td>
      </tr>`,
    )
    .join('');

  const docMeta = isReceipt
    ? `<dt>เลขที่</dt><dd>${escapeHtml(docNo)}</dd>
       <dt>วันที่</dt><dd>${thaiDate(docDate)}</dd>
       <dt>อ้างอิง</dt><dd>${escapeHtml(input.invoiceRef)}</dd>`
    : `<dt>เลขที่</dt><dd>${escapeHtml(docNo)}</dd>
       <dt>วันที่</dt><dd>${thaiDate(docDate)}</dd>
       ${input.dueAt ? `<dt>ยืนราคาถึง</dt><dd>${thaiDate(input.dueAt)}</dd>` : ''}`;

  const bankBlock = `
    <dl class="kv">
      <dt>ธนาคาร</dt><dd>${escapeHtml(seller.bank.name)}</dd>
      <dt>ชื่อบัญชี</dt><dd>${escapeHtml(seller.bank.accountName)}</dd>
      <dt>เลขที่บัญชี</dt><dd class="acct">${escapeHtml(seller.bank.accountNumber)}</dd>
    </dl>`;

  const leftColumn = isReceipt
    ? `<div class="section-title">รายละเอียดการชำระเงิน</div>
       <dl class="kv">
         <dt>ชำระโดย</dt><dd>${escapeHtml(input.paymentMethod || 'โอนเงินเข้าบัญชีธนาคาร')}</dd>
         <dt>วันที่ชำระ</dt><dd>${thaiDate(input.paidAt)}</dd>
         <dt>เข้าบัญชี</dt><dd>${escapeHtml(seller.bank.name)} ${escapeHtml(seller.bank.accountNumber)}</dd>
         <dt>อ้างอิง</dt><dd>${escapeHtml(input.invoiceRef)}</dd>
       </dl>`
    : `<div class="section-title">เงื่อนไขการชำระเงิน</div>
       <div>ชำระเต็มจำนวน${input.dueAt ? `ภายในวันที่ ${thaiDate(input.dueAt)}` : ''} โดยโอนเข้าบัญชีบริษัทตามรายละเอียดด้านล่าง
       แล้วส่งสลิปพร้อมเลขที่เอกสาร <b>${escapeHtml(input.invoiceRef)}</b> ที่ LINE OA <b>${escapeHtml(seller.lineOa)}</b></div>
       ${bankBlock}`;

  const notes = isReceipt
    ? [
        'ใบเสร็จรับเงินฉบับนี้จะสมบูรณ์เมื่อบริษัทได้รับเงินตามจำนวนข้างต้นเรียบร้อยแล้ว',
        `การใช้บริการเป็นไปตามข้อกำหนดการให้บริการของ HitzLink (${seller.website}/terms)`,
        'เอกสารนี้ออกโดยระบบอิเล็กทรอนิกส์',
      ]
    : [
        `การใช้บริการเป็นไปตามข้อกำหนดการให้บริการและนโยบายความเป็นส่วนตัวของ HitzLink (${seller.website}/terms)`,
        'แพ็กเกจ/บริการจะเปิดใช้งานหลังบริษัทตรวจสอบยอดชำระเรียบร้อยแล้ว',
        'หากไม่ชำระภายในกำหนด เอกสารนี้จะถูกยกเลิกโดยอัตโนมัติ',
      ];
  if (input.notes) notes.push(input.notes);

  const signatures = isReceipt
    ? `<div class="signs">
         <div>
           <div class="sign-line"></div>
           <div class="sign-role">ผู้จ่ายเงิน</div>
           <div class="sign-meta">${escapeHtml(c.name)}</div>
           <div class="sign-meta">วันที่ ${thaiDate(input.paidAt)}</div>
         </div>
         <div>
           <div class="sign-line"></div>
           <div class="sign-role">ผู้รับเงิน</div>
           <div class="sign-meta">ในนาม ${escapeHtml(seller.nameTh)}</div>
           <div class="sign-meta">วันที่ ${thaiDate(input.paidAt)}</div>
         </div>
       </div>`
    : `<div class="signs">
         <div>
           <div class="sign-line"></div>
           <div class="sign-role">ผู้เสนอราคา</div>
           <div class="sign-meta">ในนาม ${escapeHtml(seller.nameTh)}</div>
           <div class="sign-meta">วันที่ ........../........../..........</div>
         </div>
         <div>
           <div class="sign-line"></div>
           <div class="sign-role">ผู้อนุมัติสั่งซื้อ</div>
           <div class="sign-meta">ในนาม ${escapeHtml(c.name)}</div>
           <div class="sign-meta">วันที่ ........../........../..........</div>
         </div>
       </div>`;

  const footer = isReceipt
    ? `เอกสารนี้เป็นใบเสร็จรับเงิน ไม่ใช่ใบกำกับภาษี · ${escapeHtml(seller.nameEn)} · ${escapeHtml(docNo)}`
    : `เอกสารนี้เป็นใบเสนอราคา/ใบแจ้งหนี้ ไม่ใช่ใบกำกับภาษีหรือใบเสร็จรับเงิน · ${escapeHtml(seller.nameEn)} · ${escapeHtml(docNo)}`;

  const title = isReceipt ? 'ใบเสร็จรับเงิน' : 'ใบเสนอราคา / ใบแจ้งหนี้';
  const subtitle = isReceipt ? 'RECEIPT' : 'QUOTATION / INVOICE';

  return `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)} ${escapeHtml(docNo)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>${STYLES}</style>
</head>
<body>
${options.toolbar ? `<div class="toolbar"><button type="button" onclick="window.print()">พิมพ์ / บันทึกเป็น PDF</button></div>` : ''}
<div class="page">
  <header class="head">
    <div class="seller">
      <img src="${escapeHtml(seller.logoUrl)}" alt="${escapeHtml(seller.nameEn)}">
      <div>
        <div class="seller-th">${escapeHtml(seller.nameTh)}</div>
        <div class="seller-en">${escapeHtml(seller.nameEn)}</div>
        <div class="seller-meta">${escapeHtml(seller.address)}</div>
        <div class="seller-meta" style="margin-top:2px">เลขประจำตัวผู้เสียภาษี ${escapeHtml(seller.taxId)} (${escapeHtml(seller.branch)})</div>
        <div class="seller-meta" style="margin-top:0">${escapeHtml(seller.website)}${seller.email ? ` · ${escapeHtml(seller.email)}` : ''}${seller.phone ? ` · โทร ${escapeHtml(seller.phone)}` : ''}</div>
      </div>
    </div>
    <div class="doc">
      <div class="doc-title">${title}</div>
      <div class="doc-sub">${subtitle}</div>
      <dl class="doc-meta">${docMeta}</dl>
    </div>
  </header>

  <section class="customer">
    ${isReceipt ? `<div class="stamp">ชำระเงินแล้ว<small>PAID ${thaiDate(input.paidAt)}</small></div>` : ''}
    <div class="label">${isReceipt ? 'ได้รับเงินจาก / RECEIVED FROM' : 'ลูกค้า / CUSTOMER'}</div>
    <div class="customer-name">${escapeHtml(c.name)}</div>
    <div class="customer-grid">${customerRows}</div>
  </section>

  <table class="items">
    <thead>
      <tr>
        <th class="c" style="width:44px">ลำดับ</th>
        <th>รายการ</th>
        <th class="c" style="width:56px">จำนวน</th>
        <th class="c" style="width:56px">หน่วย</th>
        <th class="r" style="width:110px;text-align:right">ราคาต่อหน่วย</th>
        <th class="r" style="width:120px;text-align:right">จำนวนเงิน (บาท)</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="summary">
    <div class="words">(${bahtText(input.total)})</div>
    <div class="totals">
      <div class="row"><span>รวมเป็นเงิน</span><span>${money(itemsSubtotal)}</span></div>
      ${discount > 0 ? `<div class="row"><span>ส่วนลด</span><span>-${money(discount)}</span></div>` : ''}
      <div class="row"><span>ยอดหลังหักส่วนลด</span><span>${money(afterDiscount)}</span></div>
      ${extra > 0 ? `<div class="row"><span>ค่าธรรมเนียมเพิ่มเติม</span><span>${money(extra)}</span></div>` : ''}
      <div class="row grand"><span>${isReceipt ? 'ยอดรับชำระทั้งสิ้น' : 'ยอดรวมทั้งสิ้น'}</span><span>${money(input.total)}</span></div>
    </div>
  </div>
  <div class="vat-note">ราคานี้ไม่มีภาษีมูลค่าเพิ่ม เนื่องจากผู้${isReceipt ? 'รับเงิน' : 'เสนอราคา'}ยังไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม</div>

  ${
    isReceipt
      ? `<div class="paid-box">บริษัทได้รับเงินจำนวน <b>${money(input.total)} บาท</b> (${bahtText(input.total)}) เรียบร้อยแล้ว</div>`
      : ''
  }

  <div class="cols">
    <div>${leftColumn}</div>
    <div>
      <div class="section-title">หมายเหตุ</div>
      <ol class="notes">${notes.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ol>
    </div>
  </div>

  <div class="spacer"></div>
  ${signatures}
  <div class="foot">${footer}</div>
</div>
${
  options.autoPrint
    ? `<script>window.addEventListener('load',function(){(document.fonts&&document.fonts.ready?document.fonts.ready:Promise.resolve()).then(function(){setTimeout(function(){window.print()},250)})});</script>`
    : ''
}
</body>
</html>`;
}

/**
 * Opens the document in a new tab (with a print button). Falls back to an
 * HTML download when popups are blocked (in-app browsers). Pass `target`
 * when the tab had to be opened synchronously before an async fetch.
 */
export function openBillingDocument(
  input: BillingDocumentInput,
  options: { autoPrint?: boolean; target?: Window | null } = {},
): void {
  const html = buildBillingDocumentHtml(input, { toolbar: true, autoPrint: options.autoPrint });
  const win = options.target === undefined ? window.open('', '_blank') : options.target;
  if (win) {
    win.document.open();
    win.document.write(html);
    win.document.close();
    return;
  }
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const docNo = input.kind === 'receipt' ? receiptNumber(input.invoiceRef) : input.invoiceRef;
  link.download = `${docNo}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
